import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";

import AppLayout from "@/components/layout/AppLayout";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import WhaZagoLogo from "@/components/brand/WhaZagoLogo";
import GlobalSupport from "@/components/support/GlobalSupport";
import { Toaster, toast } from "sonner";
import { api, clearAuthToken, getApiError, getAuthToken } from "@/lib/api";
import { shopFavicon } from "@/lib/favicon";
import { getTheme, getPalette } from "@/lib/templates";

// Plateforme orientée revendeurs : le flux d'achat client (dashboard, buy, dépôts,
// commandes, premium, parrainage) a été retiré du routage. Les fichiers de ces
// pages restent dans le repo (réactivables) mais ne sont plus importés ici.
const loadAdminPage = () => import("./pages/AdminPage");
const loadAuthPage = () => import("./pages/AuthPage");
const loadLandingPage = () => import("./pages/LandingPage");
const loadLegalPage = () => import("./pages/LegalPage");
const loadProfilePage = () => import("./pages/ProfilePage");
const loadResellerDashboard = () => import("./pages/reseller/ResellerDashboard");
const loadResellerSignupPage = () => import("./pages/reseller/ResellerSignupPage");
const loadStorefrontPage = () => import("./pages/shop/StorefrontPage");

const AdminPage = lazy(loadAdminPage);
const AuthPage = lazy(loadAuthPage);
const LandingPage = lazy(loadLandingPage);
const LegalPage = lazy(loadLegalPage);
const ProfilePage = lazy(loadProfilePage);
const ResellerDashboard = lazy(loadResellerDashboard);
const ResellerSignupPage = lazy(loadResellerSignupPage);
const StorefrontPage = lazy(loadStorefrontPage);

const USER_CACHE_KEY = "whasago.user.cache";

const readCachedUser = () => {
  try {
    const raw = window.localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user) => {
  try {
    if (!user) {
      window.localStorage.removeItem(USER_CACHE_KEY);
      return;
    }
    window.localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch {
    // noop
  }
};

const ProtectedScreen = ({ user, wallet, onLogout, children }) =>
  user ? (
    <AppLayout user={user} wallet={wallet} onLogout={onLogout}>
      {children}
    </AppLayout>
  ) : (
    <Navigate to="/auth?mode=login" replace />
  );

// Plateforme orientée revendeurs : chaque utilisateur connecté est dirigé vers
// son espace. Admin -> panel admin (config + stock + gestion revendeurs).
// Revendeur -> son tableau de bord. Sinon -> création de boutique.
const homePath = (user) => {
  if (!user) return "/auth?mode=login";
  if (user.is_admin) return "/admin";
  if (user.role === "reseller") return "/reseller";
  return "/devenir-revendeur";
};

const HomeRedirect = ({ user }) => <Navigate to={homePath(user)} replace />;

const PLATFORM_FIRST_SEG = ["", "auth", "devenir-revendeur", "terms", "privacy", "reseller", "admin", "dashboard", "profile"];

const BrandLoadingScreen = ({ testId }) => {
  // Sur une boutique revendeur (/r/<slug> ou /<slug>), on n'affiche PAS le logo du
  // site principal : juste un écran neutre, le temps que la boutique se charge.
  const segs = (typeof window !== "undefined" ? window.location.pathname : "").split("/").filter(Boolean);
  const isShop = segs[0] === "r" || (segs.length === 1 && !PLATFORM_FIRST_SEG.includes(segs[0]));
  if (isShop) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" data-testid={testId}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", border: "3px solid #e6e9ef", borderTopColor: "#6cc72f", animation: "spin 0.8s linear infinite" }} />
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#f5fbf7] px-4" data-testid={testId}>
      <WhaZagoLogo size="lg" />
    </div>
  );
};

// Domaine principal de la plateforme (ex. "numwave.net"). Si défini, tout autre
// hôte (sous-domaine d'un domaine de base type slug.xnums.com, ou domaine perso)
// est traité comme une BOUTIQUE servie en marque blanche — jamais de redirection
// vers le site principal.
const MAIN_DOMAIN = (process.env.REACT_APP_MAIN_DOMAIN || "").toLowerCase().trim();

const detectShopHost = () => {
  if (!MAIN_DOMAIN || typeof window === "undefined") return null;
  const h = (window.location.hostname || "").toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || h === MAIN_DOMAIN || h === `www.${MAIN_DOMAIN}`) {
    return null;
  }
  return h;
};

const ShopHostLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-white">
    <div style={{ width: 34, height: 34, borderRadius: "50%", border: "3px solid #e6e9ef", borderTopColor: "#6cc72f", animation: "spin 0.8s linear infinite" }} />
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

// Cache local de la résolution host -> boutique : les visites répétées
// affichent la boutique instantanément, avec revalidation en arrière-plan.
const SITE_CACHE_KEY = "numwave.site-by-host.cache";

const readCachedSite = (host) => {
  try {
    const raw = window.localStorage.getItem(SITE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.host === host && parsed.data && parsed.data.slug ? parsed.data : null;
  } catch { return null; }
};

const writeCachedSite = (host, data) => {
  try {
    // On ne met JAMAIS en cache un échec (404) — seulement une boutique valide.
    if (data && data.slug) window.localStorage.setItem(SITE_CACHE_KEY, JSON.stringify({ host, data }));
    else window.localStorage.removeItem(SITE_CACHE_KEY);
  } catch { /* noop */ }
};

// Sert une boutique sur SON propre (sous-)domaine. Résout le slug via le host,
// puis rend la boutique à la racine (/, /login, /privacy…). Aucune mention du
// site principal : si le host ne correspond à aucune boutique, page neutre.
function ShopHostApp({ host }) {
  // undefined = en cours, null = introuvable ; sinon payload {slug, shop_name, ...}
  const [site, setSite] = useState(() => readCachedSite(host) || undefined);

  useEffect(() => {
    let cancelled = false;
    // Le chunk de la boutique se télécharge EN PARALLÈLE de la résolution du
    // host (au lieu d'attendre la réponse API) : une étape réseau de gagnée.
    loadStorefrontPage();
    // Sur un host de site revendeur, on ne montre JAMAIS le favicon Numwave :
    // favicon neutre pendant la résolution, puis celui du site (logo ou initiale).
    const link = document.getElementById("app-favicon");
    const applyIdentity = (d) => {
      if (link && d.slug) {
        const accent = getPalette(getTheme(d.template)).accent;
        link.setAttribute("href", shopFavicon(d, accent));
        link.setAttribute("type", d.logo_url ? "image/png" : "image/svg+xml");
      }
      if (d.shop_name) document.title = d.shop_name;
    };
    const cached = readCachedSite(host);
    if (cached) applyIdentity(cached);
    else if (link) {
      link.setAttribute("href", "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E");
      link.setAttribute("type", "image/svg+xml");
    }
    // Revalidation (ou première résolution) — met à jour cache et affichage.
    api.get("/public/site-by-host", { params: { host } })
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        writeCachedSite(host, d);
        setSite(d.slug ? d : null);
        if (d.slug) applyIdentity(d);
      })
      .catch((e) => {
        if (cancelled) return;
        // 404 = boutique inexistante ; autre erreur réseau : on garde le cache.
        if (e?.response?.status === 404) { writeCachedSite(host, null); setSite(null); }
        else if (!cached) setSite(null);
      });
    return () => { cancelled = true; };
  }, []);

  const slug = site === undefined ? undefined : (site ? site.slug : null);

  if (slug === undefined) return <ShopHostLoader />;
  if (!slug) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-white px-6 text-center">
        <p className="text-lg font-semibold text-zinc-800">Site introuvable</p>
        <p className="text-sm text-zinc-500">Aucun site n'est associé à cette adresse.</p>
      </div>
    );
  }

  const shopEl = <StorefrontPage slugOverride={slug} basePath="" initialShop={site} />;

  return (
    <ThemeProvider attribute="class" forcedTheme="light">
      <BrowserRouter>
        <Suspense fallback={<ShopHostLoader />}>
          <Routes>
            <Route path="/" element={shopEl} />
            <Route path="/login" element={shopEl} />
            <Route path="/register" element={shopEl} />
            <Route path="/reset" element={shopEl} />
            <Route path="/privacy" element={shopEl} />
            <Route path="/terms" element={shopEl} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        {/* GlobalSupport utilise useLocation() -> il DOIT être dans le Router. */}
        <GlobalSupport />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </ThemeProvider>
  );
}

function App() {
  const [bootLoading, setBootLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState({ balance_fcfa: 0, unit_price_fcfa: 500 });
  const [announcement, setAnnouncement] = useState({
    enabled: false,
    text: "",
    button_text: "",
    button_link: "",
  });

  const refreshWallet = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const response = await api.get("/wallet");
      setWallet(response.data);
    } catch (_error) {
      setWallet({ balance_fcfa: 0, unit_price_fcfa: 500 });
    }
  }, []);

  const loadSession = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      writeCachedUser(null);
      setUser(null);
      setWallet({ balance_fcfa: 0, unit_price_fcfa: 500 });
      setBootLoading(false);
      return;
    }

    const cachedUser = readCachedUser();
    if (cachedUser) {
      setUser(cachedUser);
      setBootLoading(false);
      refreshWallet();
    }

    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      writeCachedUser(response.data);
      setBootLoading(false);
      refreshWallet();
      return;
    } catch (error) {
      clearAuthToken();
      toast.error(getApiError(error, "Session expirée"));
      writeCachedUser(null);
      setUser(null);
      setWallet({ balance_fcfa: 0, unit_price_fcfa: 500 });
    } finally {
      setBootLoading(false);
    }
  }, [refreshWallet]);

  const handleAuthSuccess = async (userPayload) => {
    setUser(userPayload);
    writeCachedUser(userPayload);
    refreshWallet();
  };

  const handleLogout = () => {
    clearAuthToken();
    writeCachedUser(null);
    setUser(null);
    setWallet({ balance_fcfa: 0, unit_price_fcfa: 500 });
    toast.success("Déconnexion réussie");
  };

  useEffect(() => {
    let cancelled = false;
    const loadAnnouncement = async () => {
      try {
        const response = await api.get("/site/announcement");
        if (cancelled) return;
        setAnnouncement({
          enabled: Boolean(response.data?.enabled),
          text: String(response.data?.text || ""),
          button_text: String(response.data?.button_text || ""),
          button_link: String(response.data?.button_link || ""),
        });
      } catch {
        // noop: si l'API échoue, on n'affiche simplement pas la bannière
      }
    };

    loadAnnouncement();
    const intervalId = window.setInterval(loadAnnouncement, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    const removeBrandBadge = () => {
      const directBadge = document.querySelector("#emergent-badge");
      if (directBadge) {
        directBadge.remove();
      }

      const links = Array.from(document.querySelectorAll("a"));
      links.forEach((link) => {
        const href = (link.getAttribute("href") || "").toLowerCase();
        const text = (link.textContent || "").toLowerCase();
        if (
          href.includes("utm_source=emergent-badge") ||
          href.includes("app.emergent.sh") ||
          text.includes("made with emergent")
        ) {
          link.remove();
        }
      });
    };

    removeBrandBadge();
    const retryTimeout = window.setTimeout(removeBrandBadge, 1200);

    return () => {
      window.clearTimeout(retryTimeout);
    };
  }, []);

  useEffect(() => {
    const resizeObserverBlocker = (event) => {
      const message = String(event?.message || event?.reason?.message || "");
      if (
        message.includes("ResizeObserver loop completed with undelivered notifications") ||
        message.includes("ResizeObserver loop limit exceeded")
      ) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener("error", resizeObserverBlocker);
    window.addEventListener("unhandledrejection", resizeObserverBlocker);

    return () => {
      window.removeEventListener("error", resizeObserverBlocker);
      window.removeEventListener("unhandledrejection", resizeObserverBlocker);
    };
  }, []);

  useEffect(() => {
    const preloadPublic = [loadLandingPage, loadAuthPage, loadLegalPage];
    const timeoutId = window.setTimeout(() => {
      preloadPublic.forEach((loader) => {
        loader().catch(() => null);
      });
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!user) return;

    const preloadAuthenticated = [loadProfilePage, loadResellerDashboard];
    if (user?.is_admin) {
      preloadAuthenticated.push(loadAdminPage);
    }

    const timeoutId = window.setTimeout(() => {
      preloadAuthenticated.forEach((loader) => {
        loader().catch(() => null);
      });
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [user]);

  // Sur une boutique, on ne bloque pas derrière le boot du site principal :
  // la boutique se monte tout de suite et affiche SON propre écran de chargement.
  const bootSegs = (typeof window !== "undefined" ? window.location.pathname : "").split("/").filter(Boolean);
  const isShopRoute = bootSegs[0] === "r" || (bootSegs.length === 1 && !PLATFORM_FIRST_SEG.includes(bootSegs[0]));
  if (bootLoading && !isShopRoute) {
    return <BrandLoadingScreen testId="app-boot-loading" />;
  }

  const showAnnouncement = Boolean(announcement.enabled && announcement.text);

  return (
    <ThemeProvider attribute="class" forcedTheme="light">

      {showAnnouncement && (
        <AnnouncementBanner
          message={announcement.text}
          link={announcement.button_link}
          linkText={announcement.button_text}
        />
      )}

      <div style={{ paddingTop: showAnnouncement ? "40px" : "0px" }}>
        <BrowserRouter>
          <Suspense fallback={<BrandLoadingScreen testId="app-route-loading" />}>
            <Routes>
              <Route path="/" element={<LandingPage isAuthenticated={Boolean(user)} />} />
              <Route path="/terms" element={<LegalPage />} />
              <Route path="/privacy" element={<LegalPage />} />
              <Route path="/r/:slug" element={<StorefrontPage />} />
              <Route path="/r/:slug/login" element={<StorefrontPage />} />
              <Route path="/r/:slug/register" element={<StorefrontPage />} />
              <Route path="/r/:slug/reset" element={<StorefrontPage />} />
              <Route path="/r/:slug/privacy" element={<StorefrontPage />} />
              <Route path="/r/:slug/terms" element={<StorefrontPage />} />
              <Route
                path="/devenir-revendeur"
                element={
                  user?.role === "reseller"
                    ? <Navigate to="/reseller" replace />
                    : <ResellerSignupPage onAuthSuccess={handleAuthSuccess} />
                }
              />
              <Route
                path="/reseller"
                element={
                  user?.role === "reseller"
                    ? <ResellerDashboard user={user} onLogout={handleLogout} />
                    : user
                      ? <Navigate to="/dashboard" replace />
                      : <Navigate to="/auth?mode=login" replace />
                }
              />
              <Route
                path="/auth"
                element={
                  user
                    ? <HomeRedirect user={user} />
                    : <AuthPage onAuthSuccess={handleAuthSuccess} />
                }
              />
              {/* Flux d'achat client retiré du site principal (plateforme revendeurs).
                  L'achat de numéros se fait uniquement via les boutiques /r/<slug>.
                  Ces routes redirigent vers l'espace de l'utilisateur. Le code des
                  pages reste dans git, donc réactivable si besoin. */}
              <Route path="/dashboard" element={<HomeRedirect user={user} />} />
              <Route path="/buy/services" element={<HomeRedirect user={user} />} />
              <Route path="/buy/countries" element={<HomeRedirect user={user} />} />
              <Route path="/buy/checkout" element={<HomeRedirect user={user} />} />
              <Route path="/buy/tracking/:orderId" element={<HomeRedirect user={user} />} />
              <Route path="/buy" element={<HomeRedirect user={user} />} />
              <Route path="/deposits" element={<HomeRedirect user={user} />} />
              <Route path="/orders" element={<HomeRedirect user={user} />} />
              <Route path="/premium-numbers" element={<HomeRedirect user={user} />} />
              <Route path="/referral" element={<HomeRedirect user={user} />} />
              <Route
                path="/profile"
                element={
                  <ProtectedScreen user={user} wallet={wallet} onLogout={handleLogout}>
                    <ProfilePage user={user} />
                  </ProtectedScreen>
                }
              />
              <Route
                path="/admin"
                element={
                  user?.is_admin ? (
                    <ProtectedScreen user={user} wallet={wallet} onLogout={handleLogout}>
                      <AdminPage onWalletRefresh={refreshWallet} />
                    </ProtectedScreen>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              {/* Wildcard boutique revendeur : localhost/<slug-boutique> (test local).
                  En production on basculera sur des sous-domaines. Doit rester
                  l'avant-dernière route, juste avant le catch-all. */}
              <Route path="/:slug" element={<StorefrontPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <GlobalSupport />
        </BrowserRouter>

        <Toaster
          theme="dark"
          richColors
          toastOptions={{
            classNames: {
              toast: "bg-zinc-900 text-zinc-100 border border-zinc-700",
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
}

// Aiguillage racine : si on est sur le domaine d'une boutique (slug.xnums.com ou
// domaine perso), on rend la boutique en marque blanche ; sinon l'app plateforme.
// (Décision hors de App() pour ne pas appeler les hooks conditionnellement.)
export default function Root() {
  const shopHost = detectShopHost();
  if (shopHost) return <ShopHostApp host={shopHost} />;
  return <App />;
}